import React, { useEffect, useState } from "react";
import styled from "styled-components";
import appConfig from "../../lib/appConfig";
import colours from "../../lib/colours";
import mapConfig from "../../lib/mapConfig";
import { LegendItem, isLegendItemVisibleAtScale } from "../../utils/mapUtils";
import CloseButton from "../atoms/CloseButton";
import { FilledButtonAlt } from "../atoms/FilledButton";
import IconControl from "../atoms/Icon";

const estimatedMaxSecondaryItemHeight = 32; // for estimating container max height

const StyledLegendContainer = styled.div`
  position: absolute;
  min-width: 100px;
  max-width: 90%;
  min-height: 40px;
  right: 15px;
  background-color: rgba(255, 255, 255, 1);
  z-index: 2;
  color: ${colours.white};
  box-shadow: 0px 0px 12px 0px rgba(0, 0, 0, 0.25);
  margin-top: 16px;
  max-height: calc(100% - 88px);
  overflow: auto;
  display: flex;
  flex-direction: column;
  @media (max-width: 1400px) {
    top: 54px;
  }
`;

const StyledLegendButton = styled(FilledButtonAlt)`
  border-radius: 0;
  min-width: 100px;
  box-shadow: 0px 0px 6px 0px rgba(0, 0, 0, 0.25);
`;

const StyledHeadingContainer = styled.div`
  text-align: left;
  padding: 10px 10px 5px 20px;
  max-width: 342px;
  color: ${colours.th1};
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`;

const StyledHeading = styled.h1`
  font-weight: 400;
  font-size: 20px;
  color: ${colours.th1};
  line-height: 47px;
  margin: 0;
  float: left;
`;

const StyledContainer = styled.div`
  text-align: left;
  padding-right: 16px;
  padding-left: 20px;
  padding-bottom: 20px;
  max-width: 342px;
  color: ${colours.th1};
  overflow: auto;
`;

const StyledLayerGroupContainer = styled.div`
  margin-block: 16px;
  & + & {
    margin-top: 28px;
  }
`;

const StyledCloseButtonContainer = styled.div`
  float: right;
`;

const StyledLegendGroupHeading = styled.div`
  font-weight: 400;
  color: ${colours.th1l2};
  margin-bottom: 8px;
  text-transform: uppercase;
`;

const StyledLegendItemHeading = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 32px;
  cursor: default;
`;

const StyledLegendItemExpandableHeading = styled(StyledLegendItemHeading)`
  cursor: pointer;
  &:hover {
    background-color: ${colours.offWhite};
  }
`;

const StyledLegendItemHeadingTop = styled.div`
  font-weight: bold;
  font-size: 14px;
  display: flex;
  flex-direction: row;
  align-items: center;
  flex: 1;
  min-height: 32px;
`;

const StyledLegendItemHeadingBottom = styled.div`
  display: flex;
  height: 20px;
  width: 130px;
  margin-left: 25px;
  transition: max-height 0.1s;
  overflow: hidden;
  position: relative;
  top: -5px;
`;

const StyledRange = styled.input``;

const StyledLegendItemContainer = styled.div`
  margin-bottom: 5px;
`;

const StyledLegendItemCheckboxContainer = styled.div``;

const StyledLegendItemCheckbox = styled.input`
  cursor: pointer;
  vertical-align: middle;
  width: 18px;
  height: 18px;
`;

const StyledLegendItemLabel = styled.label`
  cursor: inherit;
  font-weight: 600;
  flex: 1;
  margin-left: 3px;
`;

const StyledSingleItemLegendItemLabel = styled(StyledLegendItemLabel)`
  margin-left: 4px;
`;

const StyledMultiItemSwatchContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledSecondaryLegendItemLabel = styled.label`
  font-size: 12px;
  font-weight: 400;
  color: ${colours.darkerGrey};
`;

const StyledSingleItemLegendSwatchContainer = styled.div`
  width: 20px;
  position: relative;
  top: 1px;
  margin-top: 2px;
`;

const StyledMultiItemLegendSwatchContainer = styled.div`
  width: 20px;
  margin-left: 25px;
  margin-right: 8px;
  position: relative;
  top: 2px;
`;

const StyledLegendSwatch = styled.img`
  height: 20px;
  width: 20px;
`;

const StyledChevronButton = styled.button`
  height: 100%;
  padding-top: 3px;
  border: none;
  background-color: transparent;
  text-align: center;
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const StyledSecondaryItemsContainer = styled.div`
  overflow: hidden;
  transition: max-height 0.3s;
`;

interface IProps {
  items: LegendItem[];
  toggleLayerVisibility: any;
  updateOpacity: any;
  mapScale: number;
  onOpen: any;
}

function MapLegend({
  items,
  toggleLayerVisibility,
  updateOpacity,
  mapScale,
  onOpen,
}: IProps) {
  const [open, setOpen] = useState(false);
  const [expandStates, setExpandStates] = useState<boolean[]>([]);

  useEffect(() => {
    if (items.length && !expandStates.length) {
      setExpandStates(items.map((x) => false));
    }
  }, [items, expandStates]);

  const handleClose = () => setOpen(false);
  const handleOpen = () => {
    onOpen();
    setOpen(true);
  };

  const getLegendGroups = () => {
    return Array.from(new Set(items.map((x) => x.legendGroup)));
  };

  const getEstimatedSecItemContainerHeight = (secItems: any[]) => {
    return `${secItems.length * estimatedMaxSecondaryItemHeight}px`;
  };

  const isItemVisibleAtScale = (item: any): boolean => {
    if (item.minScale === 0 && item.maxScale === 0) {
      return true;
    }
    if (item.minScale !== 0 && item.minScale < mapScale) {
      return false;
    }
    if (item.maxScale !== 0 && item.maxScale > mapScale) {
      return false;
    }
    return true;
  };

  const toggleExpander = (layerId: number) => {
    let newExpandStates = [...expandStates];
    newExpandStates[layerId] = !newExpandStates[layerId];
    setExpandStates(newExpandStates);
  };

  const renderItemLabel = (item: any) => {
    return isLegendItemVisibleAtScale(item, mapScale) ? (
      item.layerName
    ) : (
      <span title="Zoom in to see this layer">{item.layerName}</span>
    );
  };

  const renderCheckBox = (item: any) => {
    return (
      <StyledLegendItemCheckboxContainer>
        <StyledLegendItemCheckbox
          type="checkbox"
          id={`chk${item.layerId}`}
          checked={item.visible}
          onChange={() => {
            toggleLayerVisibility(item.layerId);
          }}
          onClick={(event) => {
            event.stopPropagation();
          }}
        />
      </StyledLegendItemCheckboxContainer>
    );
  };

  const renderExpander = (hasExpander: boolean, expanded: boolean) => {
    const chevronRotation = expanded ? -180 : 0;
    const iconStyle = {
      transform: `rotate(${chevronRotation}deg)`,
      transition: "transform 0.3s",
      marginRight: 0,
    };
    if (!hasExpander) return null;
    return (
      <StyledChevronButton>
        <IconControl icon="chevron-down" style={iconStyle} />
      </StyledChevronButton>
    );
  };

  const renderSecondaryItems = (secondaryItems: any[]) => {
    return secondaryItems.map((secondaryItem: any) => {
      return (
        <StyledMultiItemSwatchContainer key={secondaryItem.label}>
          <StyledMultiItemLegendSwatchContainer>
            {secondaryItem.swatch ? (
              <StyledLegendSwatch src={secondaryItem.swatch} />
            ) : null}
          </StyledMultiItemLegendSwatchContainer>
          <StyledSecondaryLegendItemLabel>
            {secondaryItem.label}
          </StyledSecondaryLegendItemLabel>
        </StyledMultiItemSwatchContainer>
      );
    });
  };

  const renderLegendItem = (item: any) => {
    const hasExpander =
      item.secondaryItems.length >= appConfig.minItemsForExpander;
    const expanded = expandStates[item.layerId];
    const showOpacityCtrl =
      mapConfig.mapServiceConfig.layersWithOpacityControl.includes(
        item.layerId
      ) && item.visible;
    const defaultOpacity =
      mapConfig.mapServiceConfig.defaultOpacities[item.layerId];
    const showSecondaryItems = !hasExpander || expanded;
    const isSingleItem = !item.secondaryItems.length;
    const itemSwatch = (
      <StyledSingleItemLegendSwatchContainer>
        {item.swatch ? <StyledLegendSwatch src={item.swatch} /> : null}
      </StyledSingleItemLegendSwatchContainer>
    );
    const visibleIcon = isItemVisibleAtScale(item) ? null : (
      <IconControl
        icon="eye-slash"
        style={{ paddingRight: 4, paddingLeft: 2 }}
      />
    );
    const HeadingComponent = hasExpander
      ? StyledLegendItemExpandableHeading
      : StyledLegendItemHeading;
    const LabelComponent = isSingleItem
      ? StyledSingleItemLegendItemLabel
      : StyledLegendItemLabel;

    return (
      <StyledLegendItemContainer key={item.layerId}>
        <HeadingComponent onClick={() => toggleExpander(item.layerId)}>
          <StyledLegendItemHeadingTop>
            {renderCheckBox(item)}
            {isSingleItem ? itemSwatch : null}
            {visibleIcon}
            <LabelComponent>{renderItemLabel(item)}</LabelComponent>
            {renderExpander(hasExpander, expanded)}
          </StyledLegendItemHeadingTop>
          <StyledLegendItemHeadingBottom
            style={{ maxHeight: showOpacityCtrl ? "20px" : 0 }}
          >
            {showOpacityCtrl && (
              <StyledRange
                type="range"
                defaultValue={defaultOpacity * 100}
                min={0}
                max={100}
                onChange={(event) =>
                  updateOpacity(item.layerId, Number(event.target.value) / 100)
                }
                onClick={(event) => event.stopPropagation()}
              />
            )}
          </StyledLegendItemHeadingBottom>
        </HeadingComponent>
        <StyledSecondaryItemsContainer
          style={{
            maxHeight: showSecondaryItems
              ? getEstimatedSecItemContainerHeight(item.secondaryItems)
              : 0,
          }}
        >
          {renderSecondaryItems(item.secondaryItems)}
        </StyledSecondaryItemsContainer>
      </StyledLegendItemContainer>
    );
  };

  const renderLayersWindow = () => {
    return (
      <StyledLegendContainer style={{ zIndex: open ? 6 : 2 }}>
        <StyledHeadingContainer>
          <StyledHeading>LAYERS</StyledHeading>
          <StyledCloseButtonContainer>
            <CloseButton onClick={handleClose} />
          </StyledCloseButtonContainer>
        </StyledHeadingContainer>
        <StyledContainer>
          {getLegendGroups().map((legendGroup) => {
            return (
              <StyledLayerGroupContainer key={legendGroup}>
                <StyledLegendGroupHeading>
                  {legendGroup}
                </StyledLegendGroupHeading>
                {items
                  .filter((item) => item.legendGroup === legendGroup)
                  .sort((a, b) => a.legendOrder - b.legendOrder)
                  .map((item) => renderLegendItem(item))}
              </StyledLayerGroupContainer>
            );
          })}
        </StyledContainer>
      </StyledLegendContainer>
    );
  };

  const renderLayersButton = () => {
    return (
      <StyledLegendContainer>
        <StyledLegendButton onClick={handleOpen}>Layers</StyledLegendButton>
      </StyledLegendContainer>
    );
  };

  return open ? renderLayersWindow() : renderLayersButton();
}

export default MapLegend;
